/**
 * storageEstimate.js — device storage headroom probe for the field zone.
 *
 * Lives in db/ because it is a storage-domain module, beside fieldDb.js (the
 * Dexie database) and fieldStore.js (the persistence layer). The field area
 * has no utils/ or lib/ directory; storage questions belong here.
 *
 * WHY THIS EXISTS: syncQueue carries base64 photo payloads (media_add is the
 * only bulk item type) and nothing anywhere in the app measured storage before
 * this — no estimate, no ceiling, no QuotaExceededError handler. A rep can fill
 * a device with queued captures and the first symptom is a failed enqueue.
 * This is the read side of making that visible.
 *
 * Raw bytes only — NO formatting. Callers decide units, because the sheet wants
 * MB and future consumers (a chip warning, a hard ceiling) may want otherwise.
 *
 * NOT reactive. navigator.storage.estimate() is a one-shot async read, so
 * callers should invoke it on open/mount, never inside a liveQuery.
 */

/**
 * @returns {Promise<{supported: boolean, usage: number|null,
 *                    quota: number|null, available: number|null}>}
 *   supported false + all nulls when the API is missing OR the estimate
 *   rejects. NEVER throws — a storage probe must not be able to break a
 *   caller's render or capture path.
 */
export async function getStorageEstimate() {
  const unsupported = { supported: false, usage: null, quota: null, available: null };
  try {
    if (typeof navigator === 'undefined' || !navigator?.storage?.estimate) {
      return unsupported;
    }
    const { usage, quota } = await navigator.storage.estimate();
    // Some engines return one side only; treat a missing number as unknown
    // rather than coercing it to 0 (0 would read as "no space used/available").
    const usageNum = typeof usage === 'number' ? usage : null;
    const quotaNum = typeof quota === 'number' ? quota : null;
    return {
      supported: true,
      usage: usageNum,
      quota: quotaNum,
      available:
        usageNum != null && quotaNum != null ? Math.max(0, quotaNum - usageNum) : null,
    };
  } catch (err) {
    console.warn('[fieldStorage] getStorageEstimate failed:', err);
    return unsupported;
  }
}

export default getStorageEstimate;
